import React, { useState, useEffect } from 'react'
import ScaleBarPulse from './ScaleBarPulse'
import ScaleBarQuinary from './ScaleBarQuinary'
import TableGridPulse from './TableGridPulse'
import TableGridQuinary from './TableGridQuinary'
import PointersPulse from './PointersPulse'
import { Text, View, StyleSheet, Font, Line, Svg } from '@react-pdf/renderer'

Font.registerHyphenationCallback(word => [word]);

const styles = StyleSheet.create({
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingLeft: '5%',
    paddingRight: '5%',
    backgroundColor: "rgba(189,187,183,0.25)",
  },
  tableHeaderTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 5,
    paddingBottom: 5,
  },
  tableHeaderTitleText1: {
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: 500,
    marginRight: 5,
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottom: '1 solid rgb(226 232 240)',
  },
  tableFirstCol: {
    width: '60%',
    backgroundColor: '#ffffff',
  },
  tableFirstColAnswer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  tableFirstColAnswerNumber: {
    width: '10%',
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
  },
  tableFirstColAnswerText: {
    display: 'flex',
    flexDirection: 'row',
    width: '75%',
    paddingRight: 10,
    paddingTop: 3,
    paddingBottom: 3,
  },
  tableFirstColAnswerPoints: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '22%',
    height: '100%',
    textAlign: 'right',
    justifyContent: 'flex-end',
    paddingLeft: 5,
    paddingRight: 5,
  },
  tableLastCol: {
    width: '40%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgb(226 232 240)',
    borderBottom: '1 solid #fff',
    paddingRight: 7,
    opacity: 0.7,
  },
  tableLastColRows: {
    width: '100%',
    flexDirection: 'row',
    height: 15,
  },
  scaleBarContainer: {
    position: 'relative',
    zIndex: 50,
    display: 'flex',
    flexDirection: 'row',
  },
  gridContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: '5.5%',
    top: -1,
    display: 'flex',
    flexDirection: 'row',
    borderTop: '1 solid rgb(226 232 240)',
  },
  gridContainer2: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: 0,
    right: '5.5%',
    top: -1,
    display: 'flex',
    flexDirection: 'row',
    borderTop: '1 solid rgb(226 232 240)',
  },
  legendContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: '5%',
    paddingRight: '5%',
    marginTop: 20,
  },
  legendText: {
    fontFamily: 'Inter',
    fontSize: 8,
    fontWeight: 400,
  },
  legendLine: {
    height: 1.5,
    width: 5,
    backgroundColor: '#000',
  },
  legendDot: {
    height: 5,
    width: 5,
    backgroundColor: '#000',
    borderRadius: 20,
  }
});

const OverviewTable = ({ answersData, categoriesData, uiData, matchCategory, questionsOn, coverData }) => {

  const [matchedCategory, setMatchedCategory] = useState(null);
  const [answersFromCategory, setAnswersFromCategory] = useState([]);

  const roundToTwoDecimals = (num) => {
    if (isNaN(num) || num === null) {
      return "0.00";
    }
    const rounded = Math.round(num * 100) / 100;
    return rounded % 1 === 0 ? `${rounded}.00` : rounded.toFixed(2);
  };

  useEffect(() => {
    if (answersData && categoriesData && matchCategory) {
      const categoryData = categoriesData.find(item => item.name === matchCategory);
      setMatchedCategory(categoryData);
      const result = answersData.filter(item => item.category === matchCategory);
      setAnswersFromCategory(result);
    }
  }, [answersData, categoriesData, matchCategory]);

  const category = categoriesData.find(cat => cat.name === matchCategory);
  let profiles = category ? category.profiles : [];

  profiles = profiles.filter(profile =>
    typeof profile.points === 'number' && !isNaN(profile.points)
  );

  profiles = profiles.map(profile => ({
    ...profile,
    points: parseFloat(roundToTwoDecimals(profile.points)),
  }));

  const totalPoints = profiles.reduce((sum, profile) => sum + profile.points, 0);
  const averagePoints = profiles.length > 0
    ? parseFloat(roundToTwoDecimals(totalPoints / profiles.length))
    : 0;

  let inserted = false;
  const profilesWithAverage = profiles
    .sort((a, b) => b.points - a.points)
    .reduce((acc, profile) => {
      if (!inserted && profile.points < averagePoints) {
        acc.push({ profile: uiData.average, points: averagePoints });
        inserted = true;
      }
      acc.push(profile);
      return acc;
    }, []);

  if (!inserted) {
    profilesWithAverage.push({ profile: uiData.average, points: averagePoints });
  }

  const calculateXPosition = (point) => {
    return point !== undefined ? (point / 6) * 100 : 0;
  };

  const calculateXPositionCategory = (point) => {
    return point !== undefined ? ((point - 1) / 4) * 100 : 0;
  };

  return (
    <>
      <View style={styles.tableHeader}>
        <View style={styles.tableHeaderTitle}>
          <Text style={styles.tableHeaderTitleText1}>{uiData.header01}: {matchCategory}</Text>
        </View>
      </View>


      <View
        style={{
          position: 'relative',
          paddingLeft: '5%',
          paddingRight: '5%',
          marginTop: 40,
          marginBottom: 35,
          width: '100%',
        }}
      >

        <View style={styles.gridContainer} zIndex={9999}>
          <View style={{ width: '60%' }}></View>
          <View style={{ width: '40%' }}>
            <TableGridPulse />
            <View
              style={{
                position: 'absolute',
                top: 0,
                bottom: 25,
                left: `${calculateXPositionCategory(averagePoints) - 0.5}%`,
                borderLeftWidth: 1,
                borderLeftColor: '#3395E0',
                borderStyle: 'dashed',
                zIndex: 9999,
              }}
            ></View>
          </View>
        </View>

        {profilesWithAverage.map((entry, index) => {
          const isAverage = entry.profile === uiData.average;
          const roundedPoints = roundToTwoDecimals(entry.points);
          const xPos = ((roundedPoints - 1) / 4) * 100;

          const getBackgroundColor = (roundedPoints, averagePoints) => {
            if (roundedPoints < averagePoints - 1) {
              return 'rgb(220, 38, 38)';
            } else if (roundedPoints >= averagePoints) {
              return 'rgb(77, 124, 15)';
            } else {
              return '#eac714';
            }
          };

          return (
            <View key={index}>
              <View style={styles.tableContainer}>
                <View style={styles.tableFirstCol}>
                  <View style={styles.tableFirstColAnswer}>
                    <View style={styles.tableFirstColAnswerText}>
                      <Text
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 10,
                          fontWeight: 500,
                          lineHeight: 1.2,
                          color: isAverage ? '#3395E0' : '#000',
                        }}
                      >
                        {entry.profile}
                      </Text>
                    </View>
                    <View style={styles.tableFirstColAnswerPoints}>
                      <Text
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 10,
                          fontWeight: 700,
                          marginRight: 3,
                          color: isAverage ? '#3395E0' : '#000',
                        }}
                      >
                        {roundedPoints}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={styles.tableLastCol}>
                  <View style={styles.tableLastColRows}>
                    <View
                      style={{
                        height: 10,
                        width: 10,
                        backgroundColor: isAverage
                          ? '#3395E0'
                          : getBackgroundColor(roundedPoints, averagePoints),
                        borderRadius: '50%',
                        position: 'absolute',
                        left: `${xPos - 0.5}%`,
                        top: 2,
                      }}
                    ></View>
                  </View>
                </View>
              </View>
            </View>
          );
        })}

        <View style={styles.scaleBarContainer}>
          <View style={{ width: '60%' }}></View>
          <View style={{ width: '40%', marginBottom: 10 }}>
            <ScaleBarPulse />
          </View>
        </View>
        {matchCategory !== uiData.lowest && matchCategory !== uiData.highest && (
          <View>
            <Text
              style={{
                fontFamily: 'Inter',
                fontSize: 7,
                fontWeight: 400,
              }}
            >
              {uiData.averText}
            </Text>
          </View>
        )}
      </View>

      <View style={styles.tableHeader}>
        <View style={styles.tableHeaderTitle}>
          <Text style={styles.tableHeaderTitleText1}>{uiData.header02}: {matchCategory}</Text>
        </View>
      </View>

      <View style={styles.legendContainer}>
        <View style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <View style={styles.legendLine}></View>
            <View style={styles.legendDot}></View>
            <View style={[styles.legendLine, { marginRight: 5 }]}></View>
          </View>
          <Text style={[styles.legendText, { marginRight: 10 }]}>{uiData.minimum_short}</Text>
          <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <View style={[styles.legendLine, { backgroundColor: '#3395E0' }]}></View>
            <View style={[styles.legendDot, { backgroundColor: '#3395E0' }]}></View>
            <View style={[styles.legendLine, { marginRight: 5, backgroundColor: '#3395E0' }]}></View>
          </View>
          <Text style={[styles.legendText, { marginRight: 10 }]}>{uiData.average}</Text>
          <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <View style={[styles.legendLine, { backgroundColor: '#7CB662' }]}></View>
            <View style={[styles.legendDot, { backgroundColor: '#7CB662' }]}></View>
            <View style={[styles.legendLine, { marginRight: 5, backgroundColor: '#7CB662' }]}></View>
          </View>
          <Text style={styles.legendText}>{uiData.maximum_short}</Text>
        </View>
      </View>

      <View
        style={{
          position: 'relative',
          paddingLeft: '5%',
          paddingRight: '5%',
          marginTop: 30,
          marginBottom: 40,
          width: '100%',
        }}
      >
        <View style={styles.gridContainer} zIndex={9999}>
          <View style={{ width: '60%' }}></View>
          <View style={{ width: '40%' }}>
            <TableGridQuinary />
          </View>
        </View>

        {answersFromCategory.map(({ id, answer, points }, index) => {
          const minPoint = roundToTwoDecimals(Math.min(...points));
          const maxPoint = roundToTwoDecimals(Math.max(...points));
          const averagePoint = roundToTwoDecimals(points.reduce((acc, val) => acc + val, 0) / points.length);

          const minPointXPosition = calculateXPosition(minPoint) - 1;
          const nextEntry = answersFromCategory[index + 1];
          let nextDotXPosition = 0;

          if (nextEntry) {
            const nextMinPoint = roundToTwoDecimals(Math.min(...nextEntry.points));
            nextDotXPosition = calculateXPosition(nextMinPoint) - 1;
          }

          return (
            <View key={id}>
              <View style={styles.tableContainer}>
                <View style={styles.tableFirstCol}>
                  <View style={[styles.tableFirstColAnswer, { justifyContent: 'flex-start' }]}>
                    <View style={styles.tableFirstColAnswerNumber}>
                      <Text
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 9,
                          fontWeight: 600,
                          lineHeight: 1.1,
                        }}
                      >
                        F{(id >= 10 ? id + '.' : '0' + id + '.')}
                      </Text>
                    </View>
                    <View style={[styles.tableFirstColAnswerText, { width: '90%', height: 25, paddingBottom: 0 }]}>
                      <Text
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 8,
                          lineHeight: 1.2,
                        }}
                      >
                        {(() => {
                          const fullText = answer;
                          const maxLength = 110;
                          return fullText.length > maxLength ? fullText.substring(0, maxLength) + '...' : fullText;
                        })()}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={[styles.tableLastCol, { backgroundColor: 'transparent', opacity: 1, borderBottom: 'none' }]}>
                  <View style={styles.tableLastColRows}>
                    {minPoint !== undefined && (
                      <View
                        style={{
                          height: 5,
                          width: 5,
                          backgroundColor: '#000',
                          borderRadius: 20,
                          position: 'absolute',
                          left: `${minPointXPosition}%`,
                          top: 5,
                        }}
                      ></View>
                    )}
                    {nextEntry && minPoint !== undefined && nextEntry.points && (
                      <Svg height="45" width="213.5">
                        <Line
                          x1={`${minPointXPosition}%`}
                          y1="7.5"
                          x2={`${nextDotXPosition}%`}
                          y2="75%"
                          strokeWidth={1.5}
                          stroke="#000"
                        />
                      </Svg>
                    )}
                  </View>
                </View>
              </View>
            </View>
          );
        })}

        <View style={{
          position: 'absolute',
          left: '6%',
          backgroundColor: 'transparent',
          opacity: 0,
          marginTop: -1,
        }}>

          {answersFromCategory.map(({ id, points }, index) => {

            const averagePoint = roundToTwoDecimals(points.reduce((acc, val) => acc + val, 0) / points.length);
            const averagePointXPosition = calculateXPosition(averagePoint) - 2;

            const nextEntry = answersFromCategory[index + 1];
            let nextDotXPosition = 0;

            if (nextEntry) {
              const nextAveragePoint = roundToTwoDecimals(nextEntry.points.reduce((acc, val) => acc + val, 0) / nextEntry.points.length);
              nextDotXPosition = calculateXPosition(nextAveragePoint) - 2;
            }


            return (
              <View key={id} style={{
                paddingTop: 5.5,
                paddingBottom: 5.5,
              }}>
                <View style={[styles.tableContainer, { border: 'none', backgroundColor: 'transparent' }]}>
                  <View style={styles.tableFirstCol}>
                  </View>

                  <View style={[styles.tableLastCol, { backgroundColor: 'transparent', opacity: 1, borderBottom: 'none' }]}>
                    <View style={styles.tableLastColRows}>
                      {averagePoint !== undefined && (
                        <View style={{
                          height: 5,
                          width: 5,
                          backgroundColor: '#3395E0',
                          borderRadius: 20,
                          position: 'absolute',
                          left: `${averagePointXPosition}%`,
                          top: 5,
                        }}></View>
                      )}
                      {nextEntry && (
                        <Svg height='45' width='211.5'>
                          <Line
                            x1={`${averagePointXPosition}%`}
                            y1={7.5}
                            x2={`${nextDotXPosition}%`}
                            y2='75%'
                            strokeWidth={1.5}
                            stroke='#3395E0'
                          />
                        </Svg>
                      )}
                    </View>
                  </View>
                </View>
              </View>
            );
          })}
        </View>

        <View style={{
          position: 'absolute',
          left: '6.75%',
          backgroundColor: 'transparent',
          opacity: 0,
          marginTop: -1,
        }}>

          {answersFromCategory.map(({ id, points }, index) => {

            const maxPoint = roundToTwoDecimals(Math.max(...points));
            const maxPointXPosition = calculateXPosition(maxPoint) - 2;

            const nextEntry = answersFromCategory[index + 1];
            let nextDotXPosition = 0;

            if (nextEntry) {
              const nextMaxPoint = roundToTwoDecimals(Math.max(...nextEntry.points));
              nextDotXPosition = calculateXPosition(nextMaxPoint) - 2;
            }


            return (
              <View key={id} style={{
                paddingTop: 5.5,
                paddingBottom: 5.5,
              }}>
                <View style={[styles.tableContainer, { border: 'none', backgroundColor: 'transparent' }]}>
                  <View style={styles.tableFirstCol}>
                  </View>

                  <View style={[styles.tableLastCol, { backgroundColor: 'transparent', opacity: 1, borderBottom: 'none' }]}>
                    <View style={styles.tableLastColRows}>
                      {maxPoint !== undefined && (
                        <View style={{
                          height: 5,
                          width: 5,
                          backgroundColor: '#7CB662',
                          borderRadius: 20,
                          position: 'absolute',
                          left: `${maxPointXPosition}%`,
                          top: 5,
                        }}></View>
                      )}
                      {nextEntry && (
                        <Svg height='45' width='210.5'>
                          <Line
                            x1={`${maxPointXPosition}%`}
                            y1={7.5}
                            x2={`${nextDotXPosition}%`}
                            y2='75%'
                            strokeWidth={1.5}
                            stroke='#7CB662'
                          />
                        </Svg>
                      )}
                    </View>
                  </View>
                </View>
              </View>
            );
          })}
        </View>

        <View style={styles.scaleBarContainer}>
          <View style={{ width: '60%' }}></View>
          <View style={{ width: '40%', marginBottom: 10 }}>
            <ScaleBarQuinary />
          </View>
        </View>
      </View>


      {questionsOn && answersFromCategory.map(({ id, answer, points }, index) => {

        const totalPoints = points.reduce((acc, val) => acc + val, 0);
        const averagePoint = points.length > 0 ? roundToTwoDecimals(totalPoints / points.length) : 0;
        const profilesWithPoints = coverData.profile.map((profile, profileIndex) => ({
          profile,
          points: points[profileIndex],
        }));

        let inserted = false;
        const profilesWithAverage = profilesWithPoints
          .sort((a, b) => b.points - a.points)
          .reduce((acc, profile) => {
            if (!inserted && profile.points < averagePoint) {
              acc.push({ profile: uiData.average, points: averagePoint });
              inserted = true;
            }
            acc.push(profile);
            return acc;
          }, []);

        if (!inserted) {
          profilesWithAverage.push({ profile: uiData.average, points: averagePoint });
        }

        return (
          <View key={id} wrap={false} style={{ marginTop: 30 }}>
            <View
              style={{
                paddingLeft: '5%',
                paddingRight: '5%',
                width: '100%',
              }}
            >
              <Text
                style={{
                  fontFamily: 'Inter',
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.1,
                  marginBottom: 5,
                }}
              >
                {uiData.header01}: {matchCategory}
              </Text>
              <Text
                style={{
                  fontFamily: 'Inter',
                  fontSize: 10,
                  fontWeight: 400,
                  lineHeight: 1.1,
                }}
              >
                F{(id >= 10 ? id + '.' : '0' + id + '.')} {answer}
              </Text>
            </View>

            <View
              style={{
                position: 'relative',
                paddingLeft: '5%',
                paddingRight: '5%',
                marginTop: 20,
                width: '100%',
              }}
            >
              <View style={styles.gridContainer} zIndex={9999}>
                <View style={{ width: '60%' }}></View>
                <View style={{ width: '40%' }}>
                  <TableGridPulse />
                  <View
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 25,
                      left: `${calculateXPositionCategory(averagePoint) - 0.5}%`,
                      borderLeftWidth: 1,
                      borderLeftColor: '#3395E0',
                      borderStyle: 'dashed',
                      zIndex: 9999,
                    }}
                  ></View>
                </View>
              </View>

              {profilesWithAverage.map((profileWithPoints, profileIndex) => {
                const isAverage = profileWithPoints.profile === uiData.average;
                const roundedPoints = roundToTwoDecimals(profileWithPoints.points);

                const xPos = ((roundedPoints - 1) / 4) * 100;

                const getBackgroundColor = (roundedPoints, averagePoint) => {
                  if (roundedPoints < averagePoint - 1) {
                    return 'rgb(220, 38, 38)';
                  } else if (roundedPoints >= averagePoint) {
                    return 'rgb(77, 124, 15)';
                  } else {
                    return '#eac714';
                  }
                };

                return (
                  <View key={profileIndex}>
                    <View style={styles.tableContainer}>
                      <View style={styles.tableFirstCol}>
                        <View style={styles.tableFirstColAnswer}>
                          <View style={styles.tableFirstColAnswerText}>
                            <Text
                              style={{
                                fontFamily: 'Inter',
                                fontSize: 10,
                                fontWeight: 500,
                                lineHeight: 1.2,
                                color: isAverage ? '#3395E0' : '#000',
                              }}
                            >
                              {profileWithPoints.profile}
                            </Text>
                          </View>
                          <View style={styles.tableFirstColAnswerPoints}>
                            <Text
                              style={{
                                fontFamily: 'Inter',
                                fontSize: 10,
                                fontWeight: 700,
                                color: isAverage ? '#3395E0' : '#000',
                              }}
                            >
                              {roundedPoints}
                            </Text>
                          </View>
                        </View>
                      </View>
                      <View style={styles.tableLastCol}>
                        <View style={styles.tableLastColRows}>
                          <View
                            style={{
                              height: 10,
                              width: 10,
                              backgroundColor: isAverage
                                ? '#3395E0'
                                : getBackgroundColor(roundedPoints, averagePoint),
                              borderRadius: '50%',
                              position: 'absolute',
                              left: `${xPos - 0.5}%`,
                              top: 2,
                            }}
                          ></View>
                        </View>
                      </View>
                    </View>
                  </View>
                );
              })}
              <View style={styles.scaleBarContainer}>
                <View style={{ width: '60%' }}></View>
                <View style={{ width: '40%', marginBottom: 10 }}>
                  <ScaleBarPulse />
                </View>
              </View>
            </View>
          </View>
        );
      })}
    </>
  )
}

export default OverviewTable